import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { usePermission } from "hooks/usePermission";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Icons } from "assets/icons";
import { CsvIcon, ExcelIcon, formatUTCDate, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import FilterBar from "components/common/FilterBar";
import Switch from "components/common/Switch";
import { CustomerStatusBadge } from "components/common/StatusBadge";
import {
  getCustomers,
  resetFilters,
  setFilter,
  updateCustomerStatus,
} from "store/slices/leadsSlice";
import {
  CUSTOMER_STATUS_OPTIONS,
  ORDER_BY_OPTIONS,
  PLATFORM_OPTIONS,
} from "utils/constants";
import NoDataFound from "components/common/NoDataFound";
import TableShimmer from "components/common/TableShimmer";
import Paginations from "components/common/Paginations";
import { handleStatusUpdate } from "helpers/apiUtils";
import DataTable from "components/common/table/DataTable";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import { Dropdown } from "react-bootstrap";
import { Download, Ellipsis } from "lucide-react";
import DownloadExcelFile from "helpers/excelDownloadHandler";
import {
  downloadCustomerReports,
  downloadCustomerReportsExcel,
} from "store/slices/reportsDownloadSlice";
import { Menu } from "primereact/menu";
import { Button } from "primereact/button";
import { showAlert } from "store/slices/alertSlice";
import TruncatedText from "components/common/TruncatedText";
import { formatPermissions } from "../Roles/roleHelper";

const Leads = () => {
  const { t } = useTranslation();
  const { view, noCustomers } = Icons || {};
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView, canEdit } = canAction("customers");
  const exportMenuRef = useRef(null);

  const {
    items: customers,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, platform },
  } = useSelector((state) => state.Leads);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      type: "lead",
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
    };
    dispatch(getCustomers(payload));
  };

  const handleReset = useCallback(() => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: CUSTOMER_STATUS_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
      {
        key: "platform",
        placeholder: t("Platform"),
        type: "singleSelect",
        options: PLATFORM_OPTIONS,
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        key: "name",
        render: (value, item) =>
          value ? (
            <Link to={`/leads/lead-details?id=${item._id}`}>{value}</Link>
          ) : (
            "-"
          ),
      },
      {
        header: t("Phone Number"),
        key: "phone",
        render: (value) => <p className="ltr_text">{value || "-"}</p>,
      },
      {
        header: t("Last Login Date"),
        key: "lastLoggedIn",
        render: (value) => <p className="ltr_text">{formatUTCDate(value)}</p>,
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
      {
        header: t("Total Vehicles"),
        key: "totalVehicles",
        render: (value) => <div className="text-center">{value}</div>,
      },
      {
        header: t("Licence Plates"),
        key: "licensePlateNumbers",
        render: (value, item) => (
          <TruncatedText text={value?.join(", ")} />
        ),
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <CustomerStatusBadge status={value} t={t} />,
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <Switch
            checked={item.status === 1}
            handleOnChange={() => {
              handleStatusUpdate({
                _id: item._id,
                status: item.status,
                dispatch,
                action: updateCustomerStatus,
              });
            }}
            disabled={isLoading && loadingItemId === item._id}
          />
        )}
        <Link to={`/leads/lead-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link>
      </>
    ),
    [],
  );
  const handleDownloadCustomers = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "csv",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
      customerType: "lead",
    };
    dispatch(downloadCustomerReports(payload)).then((result) => {
      const data = result?.payload ?? result;

      const hasData = typeof data === "string" && data.trim().length > 0;

      if (!hasData) {
        dispatch(
          showAlert({
            text: t(`No CSV data found`),
            type: "error",
          }),
        );
        return;
      }
      DownloadExcelFile(data, "leads");
    });
  };
  const handleDownloadCustomersExcel = (action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      orderBy: isReset ? -1 : orderBy,
      exportType: "excel",
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset || platform.value === "" ? "" : platform,
      customerType: "lead",
    };
    dispatch(downloadCustomerReportsExcel(payload)).then((result) => {
      const data = result?.payload ?? result;
      const hasData = data instanceof ArrayBuffer && data.byteLength > 0;

      if (!hasData) {
        dispatch(
          showAlert({
            text: t(`No Excel data found`),
            type: "error",
          }),
        );
        return;
      }
      DownloadExcelFile(data, "leads");
    });
  };
  const exportMenuItems = [
    {
      label: t("Export as CSV"),
      icon: <CsvIcon />,
      command: handleDownloadCustomers,
    },
    {
      label: t("Export as Excel"),
      icon: <ExcelIcon />,
      command: handleDownloadCustomersExcel,
    },
  ];
  const extraActions = (
    <div className="buttons export_buttons">
      <Menu
        model={exportMenuItems}
        popup
        ref={exportMenuRef}
        id="export-menu"
      />
      <Button
        label={t("Export")}
        icon={<Download size={16} />}
        onClick={(e) => exportMenuRef.current.toggle(e)}
        aria-controls="export-menu"
        aria-haspopup
        className="p-button-outlined"
      />
    </div>
  );

  if (!canView) return <NoPermission />;

  return (
    <div className="page-content customers">
      <PageHeader title={t("Leads")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name, Phone Number")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, platform }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        rightActions={extraActions}
      />
      {loading && <TableShimmer rows={12} columns={8} />}
      {!loading && customers?.length === 0 && (
        <NoDataFound title={t("No leads found")} icon={noCustomers} />
      )}
      {!loading && customers?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={customers}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Leads;
